import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Pencil, Trash2, Sparkles, Search, CalendarClock, ArrowRightCircle, AlertTriangle, MessageCircle, Download, Upload, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { listProspectos, createProspecto, updateProspecto, deleteProspecto } from "@/lib/api";
import { fmtDate, cleanPhone, exportCSV } from "@/lib/format";
import { renderWhatsAppTemplate, DEFAULT_WHATSAPP_TEMPLATE, DEFAULT_MOTIVOS_PADRAO } from "@/lib/plans";
import BulkWhatsAppDialog from "@/components/BulkWhatsAppDialog";
import ImportarProspectosDialog from "@/components/ImportarProspectosDialog";
import { useAuth, isAdmin } from "@/lib/auth";

const emptyForm = {
  vendedor: "",
  nome: "",
  contato: "",
  localidade: "",
  motivo: "",
  observacoes: "",
  data_registro: new Date().toISOString().slice(0, 10),
  reavaliar_em: "",
  status: "lead_futuro",
  condominio: "",
};

const PROSP_CONDO_NONE = "__none__";

const STATUS_LABEL = { lead_futuro: "Lead futuro", descartado: "Descartado", convertido: "Convertido" };
const STATUS_COLOR = {
  lead_futuro: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  descartado: "bg-slate-500/15 text-slate-400 border-slate-500/30",
  convertido: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
};

const daysDiff = (iso) => {
  if (!iso) return null;
  const t = new Date(iso + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((t - today) / (1000 * 60 * 60 * 24));
};

const addDays = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function ProspectosCard({ onCountChange, onConvertToCliente, onDataChange, config, condominios = [] }) {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const template = config?.whatsapp_template || DEFAULT_WHATSAPP_TEMPLATE;
  const motivosPadrao = (config?.motivos_padrao?.length ? config.motivos_padrao : DEFAULT_MOTIVOS_PADRAO);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("todos");
  const [monthFilter, setMonthFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const availableMonths = useMemo(() => {
    const set = new Set();
    for (const p of items) {
      const key = (p.data_registro || p.created_at || "").slice(0, 7);
      if (key) set.add(key);
    }
    return [...set].sort().reverse();
  }, [items]);

  const monthLabelPT = (yyyyMm) => {
    const [y, m] = yyyyMm.split("-");
    const meses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
    return `${meses[parseInt(m, 10) - 1] || m}/${y}`;
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProspectos();
      setItems(data);
      onCountChange && onCountChange(data.length);
      onDataChange && onDataChange(data);
    } catch (err) {
      console.error("Erro ao carregar prospectos:", err);
      toast.error("Erro ao carregar prospectos");
    } finally { setLoading(false); }
  }, [onCountChange, onDataChange]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return items.filter((i) => {
      if (monthFilter) {
        const key = (i.data_registro || i.created_at || "").slice(0, 7);
        if (key !== monthFilter) return false;
      }
      if (statusFilter !== "todos" && statusFilter !== "reavaliar" && i.status !== statusFilter) return false;
      if (statusFilter === "reavaliar" && !(i.reavaliar_em && daysDiff(i.reavaliar_em) <= 0)) return false;
      if (!s) return true;
      return (
        (i.nome || "").toLowerCase().includes(s) ||
        (i.vendedor || "").toLowerCase().includes(s) ||
        (i.motivo || "").toLowerCase().includes(s) ||
        (i.localidade || "").toLowerCase().includes(s)
      );
    });
  }, [items, search, statusFilter, monthFilter]);

  const alertas = useMemo(() => {
    return items
      .filter((i) => i.status === "lead_futuro" && i.reavaliar_em && daysDiff(i.reavaliar_em) <= 7)
      .sort((a, b) => (a.reavaliar_em || "").localeCompare(b.reavaliar_em || ""));
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    const base = { ...emptyForm };
    if (!admin && user?.vendedor_name) base.vendedor = user.vendedor_name;
    setForm(base);
    setOpen(true);
  };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...emptyForm, ...item });
    setOpen(true);
  };

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.nome.trim()) { toast.error("Informe o nome do prospecto"); return; }
    if (admin && !form.vendedor.trim()) { toast.error("Informe o vendedor responsável"); return; }
    try {
      if (editing) { await updateProspecto(editing.id, form); toast.success("Prospecto atualizado"); }
      else { await createProspecto(form); toast.success("Prospecto cadastrado"); }
      setOpen(false); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este prospecto?")) return;
    try { await deleteProspecto(id); toast.success("Prospecto excluído"); load(); }
    catch { toast.error("Erro ao excluir"); }
  };

  const handleConvert = (p) => {
    if (!onConvertToCliente) {
      toast.error("Conversão indisponível");
      return;
    }
    onConvertToCliente(p, async () => {
      // callback executado após cliente ser salvo com sucesso
      try {
        await updateProspecto(p.id, { ...p, status: "convertido" });
        toast.success(`Prospecto "${p.nome}" convertido em cliente`);
        load();
      } catch (err) {
        console.error("Falha ao marcar prospecto convertido:", err);
        toast.error("Prospecto convertido, mas não foi possível atualizar seu status");
      }
    });
  };

  const setReavaliarInDays = (n) => setF("reavaliar_em", addDays(n));

  const handleWhatsApp = (p) => {
    const num = cleanPhone(p.contato);
    if (!num) {
      toast.error("Este prospecto não tem telefone cadastrado");
      return;
    }
    const msg = renderWhatsAppTemplate(template, p);
    const url = `https://wa.me/${num}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = (checked) => {
    if (checked) setSelectedIds(new Set(filtered.map((i) => i.id)));
    else setSelectedIds(new Set());
  };
  const allSelected = filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id));
  const selectedItems = filtered.filter((i) => selectedIds.has(i.id));

  const handleExport = () => {
    const rows = filtered.map((i) => ({
      ...i,
      status: STATUS_LABEL[i.status] || i.status,
      data_registro: fmtDate(i.data_registro),
      reavaliar_em: fmtDate(i.reavaliar_em),
    }));
    exportCSV(`prospectos${monthFilter ? "-" + monthFilter : ""}${statusFilter !== "todos" ? "-" + statusFilter : ""}.csv`, rows, [
      { key: "vendedor", label: "Vendedor" },
      { key: "nome", label: "Nome" },
      { key: "condominio", label: "Condomínio" },
      { key: "localidade", label: "Localidade" },
      { key: "contato", label: "Contato" },
      { key: "motivo", label: "Motivo" },
      { key: "status", label: "Status" },
      { key: "data_registro", label: "Registrado em" },
      { key: "reavaliar_em", label: "Reavaliar em" },
      { key: "observacoes", label: "Observações" },
    ]);
    toast.success(`${filtered.length} prospecto(s) exportado(s)`);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="prospectos-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Prospectos & leads futuros</h3>
        </div>
        <Badge variant="outline">Sem viabilidade agora · reavaliar depois</Badge>
      </div>

      {/* Alertas de reavaliação */}
      {alertas.length > 0 && (
        <div
          className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 flex items-start gap-3"
          data-testid="alertas-reavaliacao"
        >
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-amber-500">
              {alertas.length} prospecto(s) para reavaliar em até 7 dias
            </div>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {alertas.slice(0, 5).map((a) => {
                const d = daysDiff(a.reavaliar_em);
                const label = d < 0 ? `${Math.abs(d)}d atrasado` : d === 0 ? "hoje" : `em ${d}d`;
                return (
                  <Badge key={a.id} variant="outline" className="text-[10px]">
                    {a.nome} · {label}
                  </Badge>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome, vendedor, motivo ou localidade…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-search-prospectos"
          />
        </div>
        <Select value={monthFilter || "all"} onValueChange={(v) => setMonthFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-prospecto-month">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os meses</SelectItem>
            {availableMonths.map((m) => (
              <SelectItem key={m} value={m}>{monthLabelPT(m)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[200px]" data-testid="filter-prospecto-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os status</SelectItem>
            <SelectItem value="lead_futuro">Leads futuros</SelectItem>
            <SelectItem value="descartado">Descartados</SelectItem>
            <SelectItem value="convertido">Convertidos</SelectItem>
            <SelectItem value="reavaliar">Reavaliar hoje/atrasado</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="btn-export-prospectos">
            <Download className="w-4 h-4 mr-2" /> CSV
          </Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="btn-import-prospectos">
            <Upload className="w-4 h-4 mr-2" /> Importar
          </Button>
          {selectedItems.length > 0 && (
            <Button
              variant="outline"
              onClick={() => setBulkOpen(true)}
              className="text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10"
              data-testid="btn-bulk-whatsapp"
            >
              <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp ({selectedItems.length})
            </Button>
          )}
          <Button onClick={openCreate} data-testid="btn-add-prospecto">
            <Plus className="w-4 h-4 mr-2" /> Novo
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="thin-scroll overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleAll}
                    data-testid="checkbox-select-all-prospectos"
                    aria-label="Selecionar todos"
                  />
                </TableHead>
                <TableHead>Vendedor</TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Localidade</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Reavaliar</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">Carregando…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">Nenhum prospecto encontrado.</TableCell></TableRow>}
              {filtered.map((i) => {
                const d = daysDiff(i.reavaliar_em);
                const overdue = i.status === "lead_futuro" && d !== null && d <= 0;
                const soon = i.status === "lead_futuro" && d !== null && d > 0 && d <= 7;
                return (
                  <TableRow key={i.id} data-testid={`prospecto-row-${i.id}`}>
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.has(i.id)}
                        onCheckedChange={() => toggleSelect(i.id)}
                        data-testid={`checkbox-prospecto-${i.id}`}
                        aria-label={`Selecionar ${i.nome}`}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{i.vendedor}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{i.nome}</span>
                        {i.condominio && (
                          <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 gap-1 text-[10px]" data-testid={`prospecto-condo-${i.id}`}>
                            <Building2 className="w-2.5 h-2.5" /> <span className="max-w-[120px] truncate">{i.condominio}</span>
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{i.localidade || "—"}</TableCell>
                    <TableCell className="text-sm tabular-nums">{i.contato || "—"}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate" title={i.motivo}>{i.motivo || "—"}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_COLOR[i.status] || ""}>
                        {STATUS_LABEL[i.status] || i.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {i.reavaliar_em ? (
                        <div className="flex items-center gap-1.5">
                          <CalendarClock className={`w-3.5 h-3.5 ${overdue ? "text-red-500" : soon ? "text-amber-500" : "text-muted-foreground"}`} />
                          <span className={overdue ? "text-red-500 font-semibold" : soon ? "text-amber-500" : ""}>
                            {fmtDate(i.reavaliar_em)}
                          </span>
                          {overdue && <span className="text-[10px] text-red-500">({Math.abs(d)}d)</span>}
                          {soon && <span className="text-[10px] text-amber-500">(em {d}d)</span>}
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {cleanPhone(i.contato) && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="text-emerald-500 border-emerald-500/40 hover:bg-emerald-500/10"
                            onClick={() => handleWhatsApp(i)}
                            data-testid={`btn-whatsapp-prospecto-${i.id}`}
                          >
                            <MessageCircle className="w-3.5 h-3.5 mr-1.5" /> WhatsApp
                          </Button>
                        )}
                        {i.status !== "convertido" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleConvert(i)}
                            data-testid={`btn-convert-prospecto-${i.id}`}
                          >
                            <ArrowRightCircle className="w-3.5 h-3.5 mr-1.5" /> Converter
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" onClick={() => openEdit(i)} data-testid={`btn-edit-prospecto-${i.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleDelete(i.id)} data-testid={`btn-delete-prospecto-${i.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-prospecto">          <DialogHeader>
            <DialogTitle>{editing ? "Editar prospecto" : "Novo prospecto"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="space-y-1.5">
              <Label>Vendedor *</Label>
              <Input
                value={form.vendedor}
                onChange={(e) => setF("vendedor", e.target.value)}
                placeholder="Ex: Ana Souza"
                disabled={!admin}
                data-testid="input-prospecto-vendedor"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Nome do prospecto *</Label>
              <Input value={form.nome} onChange={(e) => setF("nome", e.target.value)} placeholder="Ex: Restaurante Alto" data-testid="input-prospecto-nome" />
            </div>
            <div className="space-y-1.5">
              <Label>Localidade</Label>
              <Input value={form.localidade} onChange={(e) => setF("localidade", e.target.value)} placeholder="Bairro / endereço" />
            </div>
            <div className="space-y-1.5">
              <Label>Contato (telefone / e-mail)</Label>
              <Input value={form.contato} onChange={(e) => setF("contato", e.target.value)} placeholder="Ex: 5511999998888" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Motivo (por que não temos viabilidade agora)</Label>
              <Input
                value={form.motivo}
                onChange={(e) => setF("motivo", e.target.value)}
                placeholder="Escolha ou digite um motivo…"
                list="motivos-suggestions"
                data-testid="input-prospecto-motivo"
              />
              <datalist id="motivos-suggestions">
                {motivosPadrao.map((m) => <option key={m} value={m} />)}
              </datalist>
            </div>
            <div className="space-y-1.5">
              <Label>Data do registro</Label>
              <Input type="date" value={form.data_registro} onChange={(e) => setF("data_registro", e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Reavaliar em</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={form.reavaliar_em}
                  onChange={(e) => setF("reavaliar_em", e.target.value)}
                  data-testid="input-reavaliar-em"
                  className="flex-1"
                />
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {[7, 15, 30, 60, 90].map((n) => (
                  <Button
                    key={n}
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-[11px] px-2"
                    onClick={() => setReavaliarInDays(n)}
                    data-testid={`btn-reavaliar-${n}d`}
                  >
                    +{n}d
                  </Button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setF("status", v)}>
                <SelectTrigger data-testid="select-prospecto-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead_futuro">Lead futuro</SelectItem>
                  <SelectItem value="descartado">Descartado</SelectItem>
                  <SelectItem value="convertido">Convertido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label>Observações</Label>
              <Textarea rows={3} value={form.observacoes} onChange={(e) => setF("observacoes", e.target.value)} placeholder="Detalhes, quando reavaliar, contexto…" />
            </div>
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-muted-foreground" />
                Condomínio (opcional)
              </Label>
              <Select
                value={form.condominio || PROSP_CONDO_NONE}
                onValueChange={(v) => setF("condominio", v === PROSP_CONDO_NONE ? "" : v)}
              >
                <SelectTrigger data-testid="select-prospecto-condominio">
                  <SelectValue placeholder="Sem vínculo com condomínio" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={PROSP_CONDO_NONE}>Sem vínculo</SelectItem>
                  {condominios.map((c) => (
                    <SelectItem key={c.id} value={c.condominio} data-testid={`opt-prosp-condo-${c.id}`}>
                      {c.condominio}
                      <span className="text-[10px] text-muted-foreground ml-2">
                        {(c.infra_status || "atendido") === "atendido" ? "atendido" : "prospecto"}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} data-testid="btn-save-prospecto">{editing ? "Salvar alterações" : "Cadastrar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkWhatsAppDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        prospectos={selectedItems}
        template={template}
      />

      <ImportarProspectosDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={load}
      />
    </div>
  );
}
